import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, isAdminRole } from '../utils/helpers';
import Linkify from 'linkify-react';

const STORAGE_KEY = 'moraartt-chat';

const ChatBotUI = ({ onLogout, onLoginRequest, onCloseLoginModal }) => {
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [pendingNav, setPendingNav] = useState(null); // { route, finalText } | 'confirm-logout'
  const [publicProducts, setPublicProducts] = useState([]); // cache productos para usuarios no logeados
  const [productsLoaded, setProductsLoaded] = useState(false);

  // Cargar conversación de la sesión actual (si no existe, saludar como bot)
  const [chatLog, setChatLog] = useState(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
    return [
      { sender: 'bot', text: '¡Hola! Soy Mora, tu asistente virtual. ¿En qué te puedo ayudar?' }
    ];
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Persistir en sessionStorage cada cambio (mientras no se recargue)
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(chatLog));
  }, [chatLog]);

  // Borrar automáticamente solo al recargar/cerrar pestaña (F5/unload)
  useEffect(() => {
    const handleBeforeUnload = () => {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatLog]);

  // También desplaza cuando el bot está "escribiendo"
  useEffect(() => {
    if (isTyping) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isTyping]);

  // Auto focus al abrir el chat (montaje del componente)
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Detecta finalización (aproximada) de navegación a una ruta objetivo
  useEffect(() => {
    if (pendingNav && typeof pendingNav === 'object' && location.pathname === pendingNav.route) {
      // Pequeña espera para que la UI de destino termine de pintar
      const t = setTimeout(() => {
        setIsTyping(false);
        if (pendingNav.finalText) {
          setChatLog(prev => [...prev, { sender: 'bot', text: pendingNav.finalText }]);
        }
        setPendingNav(null);
      }, 800);
      return () => clearTimeout(t);
    }
  }, [location.pathname, pendingNav]);

  const delay = (ms) => new Promise(res => setTimeout(res, ms));

  const getCurrentUser = () => {
    try {
      const saved = localStorage.getItem('user');
      if (!saved) return null;
      return JSON.parse(saved);
    } catch { return null; }
  };
  const isAdmin = () => {
    const u = getCurrentUser();
    return !!(u && isAdminRole(u.role));
  };
  const isLoggedIn = () => !!getCurrentUser();

  // Verifica si el usuario actual está activo en el servidor
  const ensureActiveUser = async () => {
    const u = getCurrentUser();
    if (!u) return false;
    try {
      const res = await fetch(`${API_BASE}/api/usuarios/${u.id}/status`);
      if (!res.ok) return true; // si falla, no bloquear silenciosamente
      const data = await res.json();
      if (data && data.success && String(data.estado).toLowerCase() !== 'activo') {
        // Desloguear y avisar
        setIsTyping(true); await delay(600);
        setChatLog(prev => [...prev, { sender: 'bot', text: 'Tu cuenta se encuentra inactiva. Por favor, contacta al administrador.' }]);
        if (typeof onLogout === 'function') onLogout(); else localStorage.removeItem('user');
        try { if (typeof onCloseLoginModal === 'function') onCloseLoginModal(); } catch {}
        setIsTyping(false);
        return false;
      }
    } catch {
      // si hay error de red, asumimos activo para no bloquear indebidamente
      return true;
    }
    return true;
  };


  // Intenciones
  const isUsersIntent = (text) => {
    const q = (text || '').toLowerCase();
    return /\b(usuarios?|registrad[oa]s?|lista de usuarios|ver usuarios)\b/.test(q);
  };
  const isDashboardIntent = (t) => /\b(dashboard|panel|inicio admin|admin)\b/i.test(t);
  const isProductsIntent = (t) => /\b(productos?|servicios?)\b/i.test(t);
  const isBookingsAdminIntent = (t) => /\b(reservas|bookings)\b/i.test(t);
  const isAgendaIntent = (t) => /\b(agenda|horarios?)\b/i.test(t);
  const isMisReservasIntent = (t) => /\b(mis reservas|mis turnos|mis citas)\b/i.test(t);
  const isLogoutIntent = (t) => /\b(cerrar sesi[oó]n|salir|logout)\b/i.test(t);
  const isThankYouIntent = (t) => /\b(gracias|thank\s?you|thanks)\b/i.test(t);
  const isNoMoreHelpIntent = (t) => /^(no(\s+gracias)?|por ahora no|nada|estoy bien)\.?$/i.test(t.trim());
  const isGreetingTypo = (t) => /^(ola|holas)\b/i.test(t.trim());
  const isReservationNumber = (t) => /^[A-Z]\d{4}$/i.test(t.trim());
  const isReservationWithNumber = (t) => /\breserva\s+[A-Z]\d{4}\b/i.test(t); // Detecta "reserva A0128"
  const extractReservationNumber = (t) => {
    const m = t.match(/\breserva\s+([A-Z]\d{4})\b/i);
    return m ? m[1].toUpperCase() : null;
  };
  const isRegisterIntent = (t) => /registr|crear.*cuenta|nueva.*cuenta|c[oó]mo.*registro|c[oó]mo.*ingreso|c[oó]mo.*creo|quiero.*cuenta|necesito.*cuenta|abrir.*cuenta|hacer.*cuenta|sign up|inscribir|afili|alta.*usuario|cuenta nueva|me.*registro|dar.*alta|alta de|registrate|inscribete|afiliate|nuevo.*usuario|usuario nuevo|primer.*cuenta|mi primer|abrir mi cuenta/i.test(t);
  const isForgotPasswordIntent = (t) => /olvid[eé].*contrase|recuperar.*contrase|restablecer.*contrase|no recuerdo|cambiar.*contrase|forgot|reset|reseteár|no s[eé].*contrase|contrase.*olvidada|desbloquear|recuperar acceso/i.test(t);
  const isLoginIntent = (t) => /iniciar.*sesi|login|ingresar|entrar|autenticar|c[oó]mo.*inicio|c[oó]mo.*conecto|c[oó]mo.*accedo|c[oó]mo.*entro|acceso.*usuario|usuario.*contrase|me.*conecto|conectarme|loguearse|accesar|signin|entrar al sistema|ir al login|mi sesi|sesi.*existente|usuario existente|ya tengo|ya cuento|tengo cuenta/i.test(t);

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = { sender: 'user', text: chatInput };
    setChatLog(prev => [...prev, userMessage]);
    const messageToSend = chatInput;
    setChatInput('');

    try {
      const lower = messageToSend.toLowerCase();

      // Confirmaciones pendientes (logout)
      if (pendingNav === 'confirm-logout') {
        if (/^(s[ií]|si|claro|ok|dale|affirmative|yes)\b/i.test(lower)) {
          setIsTyping(true);
          await delay(800);
          try {
            if (typeof onLogout === 'function') onLogout();
            else localStorage.removeItem('user');
          } finally {
            setIsTyping(false);
            setChatLog(prev => [...prev, { sender: 'bot', text: 'Sesión cerrada.' }]);
            setPendingNav(null);
          }
          return;
        }
        if (/^no\b/i.test(lower)) {
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Entendido, estamos aquí para ayudarte.' }]);
          setPendingNav(null);
          return;
        }
        setChatLog(prev => [...prev, { sender: 'bot', text: '¿Deseas salir de la sesión? Responde "sí" o "no".' }]);
        return;
      }

      // Corrección de saludo con error tipográfico
      if (isGreetingTypo(messageToSend)) {
        setIsTyping(true); await delay(600);
        setChatLog(prev => [...prev, { sender: 'bot', text: '¡Hola! 👋 Soy Mora, tu asistente de MoraArtt. ¿En qué te ayudo?' }]);
        setIsTyping(false);
        return;
      }

      // Respuestas cordiales simples
      if (isThankYouIntent(lower)) {
        setIsTyping(true); await delay(600);
        setChatLog(prev => [...prev, { sender: 'bot', text: '¡De nada! ¿Te ayudo con algo más?' }]);
        setIsTyping(false);
        return;
      }
      if (isNoMoreHelpIntent(lower)) {
        setIsTyping(true); await delay(600);
        setChatLog(prev => [...prev, { sender: 'bot', text: 'Estamos aquí para ayudarte.' }]);
        setIsTyping(false);
        return;
      }

      // PRIMERO: Si el usuario cambia de opinión mientras está en modal - detectar ANTES de ask-login-or-register
      if (isRegisterIntent(lower) && location.pathname !== '/register') {
        // Quiere crear cuenta pero está en otro lugar (posiblemente en modal de login)
        setIsTyping(true); await delay(600);
        try { if (typeof onCloseLoginModal === 'function') onCloseLoginModal(); } catch {}
        await delay(300);
        setChatLog(prev => [...prev, { sender: 'bot', text: 'Te llevo al registro. 📝' }]);
        setIsTyping(false);
        setPendingNav({ route: '/register', finalText: 'Formulario de registro.' });
        navigate('/register');
        return;
      }

      if (isLoginIntent(lower) && location.pathname !== '/') {
        // Quiere iniciar sesión pero está en otro lugar (posiblemente en register)
        setIsTyping(true); await delay(600);
        try { if (typeof onCloseLoginModal === 'function') onCloseLoginModal(); } catch {}
        await delay(300);
        setChatLog(prev => [...prev, { sender: 'bot', text: 'Abriendo inicio de sesión. 🔓' }]);
        setIsTyping(false);
        setPendingNav(null);
        if (typeof onLoginRequest === 'function') onLoginRequest();
        return;
      }

      // SEGUNDO: Respuesta a pregunta de login o registro (evita re-detectar intenciones)
      if (pendingNav === 'ask-login-or-register') {
        const userResponse = lower.trim();
        
        // Detectar si quiere registrarse
        if (/registr|crear|nueva|registro/i.test(userResponse)) {
          setIsTyping(true); await delay(800);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Te llevo al registro. 📝' }]);
          setIsTyping(false);
          setPendingNav({ route: '/register', finalText: 'Formulario de registro.' });
          navigate('/register');
          return;
        }
        
        // Detectar si quiere iniciar sesión
        if (/iniciar|login|ingresar|acceder|conectar|sesion/i.test(userResponse)) {
          setIsTyping(true); await delay(800);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Abriendo inicio de sesión. 🔓' }]);
          setIsTyping(false);
          setPendingNav(null);
          if (typeof onLoginRequest === 'function') onLoginRequest();
          return;
        }
        
        // Si no entiende, repite la pregunta
        setIsTyping(true); await delay(600);
        setChatLog(prev => [...prev, { sender: 'bot', text: 'Responde "crear cuenta" o "iniciar sesión".' }]);
        setIsTyping(false);
        return;
      }

      // Intención de iniciar sesión o crear cuenta - PREGUNTA PRIMERO
      if (isLoginIntent(lower) || isRegisterIntent(lower)) {
        if (isLoggedIn()) {
          setIsTyping(true); await delay(600);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Ya estás conectado como ' + getCurrentUser().nombre + '. ¿En qué te ayudo?' }]);
          setIsTyping(false);
        } else {
          setIsTyping(true); await delay(1000);
          const explicacion = `Te explico nuestras opciones:

📝 **CREAR CUENTA (REGISTRO):** Si eres nuevo en MoraArtt, puedes crear una cuenta para acceder al sistema y reservar nuestros servicios.

🔓 **INICIAR SESIÓN:** Si ya tienes cuenta, usa tus credenciales para acceder.

¿Qué deseas hacer?`;
          setChatLog(prev => [...prev, { sender: 'bot', text: explicacion }]);
          setIsTyping(false);
          setPendingNav('ask-login-or-register');
        }
        return;
      }

      // Intención de recuperar contraseña
      if (isForgotPasswordIntent(lower)) {
        if (isLoggedIn()) {
          setIsTyping(true); await delay(600);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Ya tienes sesión activa. Si quieres cambiar tu contraseña, cierra sesión primero.' }]);
          setIsTyping(false);
        } else {
          setIsTyping(true);
          setPendingNav({ route: '/restore-password', finalText: 'Recuperación de contraseña.' });
          await delay(800);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Te llevo a recuperación. 🔑' }]);
          navigate('/restore-password');
        }
        return;
      }

      // Intenciones de navegación/acciones
      if (isLogoutIntent(lower)) {
        setPendingNav('confirm-logout');
        setChatLog(prev => [...prev, { sender: 'bot', text: '¿Deseas salir de la sesión? Responde "sí" o "no".' }]);
        return;
      }

      if (isUsersIntent(lower)) {
        if (isAdmin()) {
          const ok = await ensureActiveUser(); if (!ok) return;
          setIsTyping(true);
          setPendingNav({ route: '/usuarios', finalText: 'Usuarios registrados.' });
          navigate('/usuarios');
        } else {
          setIsTyping(true); await delay(800);
          setChatLog(prev => [...prev, { sender: 'bot', text: '¿Te interesa ver nuestros servicios o productos?' }]);
          setIsTyping(false);
        }
        return;
      }

      if (isDashboardIntent(lower)) {
        if (isAdmin()) {
          const ok = await ensureActiveUser(); if (!ok) return;
          setIsTyping(true);
          setPendingNav({ route: '/admin', finalText: 'Panel de administración.' });
          navigate('/admin');
        } else {
          setIsTyping(true); await delay(800);
          setChatLog(prev => [...prev, { sender: 'bot', text: '¿Te interesa ver nuestros servicios o productos?' }]);
          setIsTyping(false);
        }
        return;
      }

      // Detección de "reserva NÚMERO" (ej: "reserva A0128")
      if (isReservationWithNumber(messageToSend)) {
        if (isLoggedIn()) {
          const ok = await ensureActiveUser(); if (!ok) return;
          const numeroReserva = extractReservationNumber(messageToSend);
          
          if (numeroReserva) {
            setIsTyping(true);
            try {
              // Consulta directa a la tabla por número de reserva
              const res = await axios.get(`${API_BASE}/api/reservas/numero/${numeroReserva}`);
              const reserva = res.data;
              
              await delay(1000);
              
              // Formatear detalles de la reserva
              const fecha = new Date(reserva.Fecha).toLocaleDateString('es-CL');
              // Formatear hora: si es timestamp/número, convertir; si es string HH:mm, usar directamente
              let hora = '—';
              if (reserva.Hora) {
                const horaStr = String(reserva.Hora);
                if (/^\d+$/.test(horaStr)) {
                  // Es un número (timestamp), convertir a HH:mm
                  const horaNum = parseInt(horaStr, 10);
                  const d = new Date(horaNum);
                  hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                } else {
                  // Es un string, tomar los primeros 5 caracteres (HH:mm)
                  hora = horaStr.slice(0, 5);
                }
              }
              const detalle = ` Reserva ${reserva.NumeroReserva}\n\n` +
                ` Servicio: ${reserva.NombreProducto || 'No especificado'}\n` +
                ` Fecha: ${fecha}\n` +
                ` Hora: ${hora}\n` +
                ` Valor: $${reserva.Precio ? Number(reserva.Precio).toLocaleString('es-CL') : '—'}\n` +
                ` Estado: ${reserva.Estado || 'pendiente'}\n` +
                `${reserva.Notas ? ` Notas: ${reserva.Notas}\n` : ''}` +
                `\n¿Quieres que te lleve a tu historial de reservas?`;
              
              setChatLog(prev => [...prev, { sender: 'bot', text: detalle }]);
              setIsTyping(false);
              setPendingNav('navigate-to-reservas');
            } catch (error) {
              console.error('Error cargando reserva:', error);
              await delay(800);
              if (error.response && error.response.status === 404) {
                setChatLog(prev => [...prev, { sender: 'bot', text: `No encontré una reserva con el número ${numeroReserva}. ¿Quieres verificar el número e intentar de nuevo?` }]);
              } else {
                setChatLog(prev => [...prev, { sender: 'bot', text: 'Lo siento, no pude cargar la información de la reserva. ¿Quieres ir directamente a tu historial?' }]);
                setPendingNav('navigate-to-reservas');
              }
              setIsTyping(false);
            }
          }
        } else {
          setIsTyping(true); await delay(1200);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Para consultar tus reservas, inicia sesión primero.' }]);
          setIsTyping(false);
          if (typeof onLoginRequest === 'function') onLoginRequest();
        }
        return;
      }

      // Detección directa de número de reserva (ej: A0121)
      if (isReservationNumber(messageToSend.trim())) {
        if (isLoggedIn()) {
          const ok = await ensureActiveUser(); if (!ok) return;
          const numeroReserva = messageToSend.trim().toUpperCase();
          
          setIsTyping(true);
          try {
            // Consulta directa a la tabla por número de reserva
            const res = await axios.get(`${API_BASE}/api/reservas/numero/${numeroReserva}`);
            const reserva = res.data;
            
            await delay(1000);
            
            // Formatear detalles de la reserva
            const fecha = new Date(reserva.Fecha).toLocaleDateString('es-CL');
            // Formatear hora: si es timestamp/número, convertir; si es string HH:mm, usar directamente
            let hora = '—';
            if (reserva.Hora) {
              const horaStr = String(reserva.Hora);
              if (/^\d+$/.test(horaStr)) {
                // Es un número (timestamp), convertir a HH:mm
                const horaNum = parseInt(horaStr, 10);
                const d = new Date(horaNum);
                hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
              } else {
                // Es un string, tomar los primeros 5 caracteres (HH:mm)
                hora = horaStr.slice(0, 5);
              }
            }
            const detalle = ` Reserva ${reserva.NumeroReserva}\n\n` +
              ` Servicio: ${reserva.NombreProducto || 'No especificado'}\n` +
              ` Fecha: ${fecha}\n` +
              ` Hora: ${hora}\n` +
              ` Valor: $${reserva.Precio ? Number(reserva.Precio).toLocaleString('es-CL') : '—'}\n` +
              ` Estado: ${reserva.Estado || 'pendiente'}\n` +
              `${reserva.Notas ? ` Notas: ${reserva.Notas}\n` : ''}` +
              `\n¿Quieres que te lleve a tu historial de reservas?`;
            
            setChatLog(prev => [...prev, { sender: 'bot', text: detalle }]);
            setIsTyping(false);
            setPendingNav('navigate-to-reservas');
          } catch (error) {
            console.error('Error cargando reserva:', error);
            await delay(800);
            if (error.response && error.response.status === 404) {
              setChatLog(prev => [...prev, { sender: 'bot', text: `No encontré una reserva con el número ${numeroReserva}. Verifica el número e intenta de nuevo.` }]);
            } else {
              setChatLog(prev => [...prev, { sender: 'bot', text: 'Lo siento, no pude cargar la información de la reserva. ¿Quieres ir directamente a tu historial?' }]);
              setPendingNav('navigate-to-reservas');
            }
            setIsTyping(false);
          }
        } else {
          setIsTyping(true); await delay(1200);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Para consultar tus reservas, inicia sesión primero.' }]);
          setIsTyping(false);
          if (typeof onLoginRequest === 'function') onLoginRequest();
        }
        return;
      }

      // PRIORIDAD: intención del cliente para sus propias reservas
      if (isMisReservasIntent(lower)) {
        if (isLoggedIn()) {
          const ok = await ensureActiveUser(); if (!ok) return;
          setIsTyping(true);
          await delay(800);
          setChatLog(prev => [...prev, { sender: 'bot', text: '¿Cuál es el número de tu reserva? (Ejemplo: A0001, A0128.)' }]);
          setIsTyping(false);
          setPendingNav('ask-reservation-number');
        } else {
          setIsTyping(true); await delay(1200);
          setChatLog(prev => [...prev, { sender: 'bot', text: 'Para ver tus reservas, inicia sesión.' }]);
          setIsTyping(false);
          if (typeof onLoginRequest === 'function') onLoginRequest();
        }
        return;
      }
      
      // Respuesta a número de reserva
      if (pendingNav === 'ask-reservation-number') {
        const numeroReserva = messageToSend.trim().toUpperCase();
        
        setIsTyping(true);
        try {
          // Consulta directa a la tabla por número de reserva
          const res = await axios.get(`${API_BASE}/api/reservas/numero/${numeroReserva}`);
          const reserva = res.data;
          
          await delay(1000);
          
          // Formatear detalles de la reserva
          const fecha = new Date(reserva.Fecha).toLocaleDateString('es-CL');
          // Formatear hora: si es timestamp/número, convertir; si es string HH:mm, usar directamente
          let hora = '—';
          if (reserva.Hora) {
            const horaStr = String(reserva.Hora);
            if (/^\d+$/.test(horaStr)) {
              // Es un número (timestamp), convertir a HH:mm
              const horaNum = parseInt(horaStr, 10);
              const d = new Date(horaNum);
              hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            } else {
              // Es un string, tomar los primeros 5 caracteres (HH:mm)
              hora = horaStr.slice(0, 5);
            }
          }
          const detalle = ` Reserva ${reserva.NumeroReserva}\n\n` +
            ` Servicio: ${reserva.NombreProducto || 'No especificado'}\n` +
            ` Fecha: ${fecha}\n` +
            ` Hora: ${hora}\n` +
            ` Valor: $${reserva.Precio ? Number(reserva.Precio).toLocaleString('es-CL') : '—'}\n` +
            ` Estado: ${reserva.Estado || 'pendiente'}\n` +
            `${reserva.Notas ? ` Notas: ${reserva.Notas}\n` : ''}` +
            `\n¿Quieres que te lleve a tu historial de reservas?`;
          
          setChatLog(prev => [...prev, { sender: 'bot', text: detalle }]);
          setIsTyping(false);
          setPendingNav('navigate-to-reservas');
        } catch (error) {
          console.error('Error cargando reserva:', error);
          await delay(800);
          if (error.response && error.response.status === 404) {
            setChatLog(prev => [...prev, { sender: 'bot', text: `No encontré una reserva con el número ${numeroReserva}. ¿Quieres verificar el número e intentar de nuevo?` }]);
          } else {
            setChatLog(prev => [...prev, { sender: 'bot', text: 'Lo siento, no pude cargar la información de la reserva. ¿Quieres ir directamente a tu historial?' }]);
            setPendingNav('navigate-to-reservas');
          }
          setIsTyping(false);
        }
        return;
      }
      
      // Confirmación para navegar a historial de reservas
      if (pendingNav === 'navigate-to-reservas') {
        if (/^(s[ií]|si|claro|ok|dale|ver|mostrar|muestra|yes)\b/i.test(lower)) {
          setIsTyping(true);
          setPendingNav({ route: '/mis-reservas', finalText: 'Tu historial de reservas.' });
          navigate('/mis-reservas');
          return;
        }
        if (/^no\b/i.test(lower)) {
          setChatLog(prev => [...prev, { sender: 'bot', text: '¿En qué más te ayudo?' }]);
          setPendingNav(null);
          return;
        }
        setChatLog(prev => [...prev, { sender: 'bot', text: '¿Ver tu historial? (sí/no)' }]);
        return;
      }

      if (isProductsIntent(lower)) {
        // Admin: navegación directa a panel de gestión
        if (isAdmin()) {
          setIsTyping(true);
          setPendingNav({ route: '/admin/products', finalText: 'Te muestro los productos.' });
          navigate('/admin/products');
          return;
        }
        
        // Para clientes: primero mostrar lista de productos activos y preguntar
        setIsTyping(true);
        
        // Cargar productos si no están cargados
        let currentProducts = publicProducts;
        if (!productsLoaded) {
          try {
            const res = await axios.get(`${API_BASE}/api/productos`);
            const activeProducts = (res.data || []).filter(p => p.Disponible === true || p.Disponible === 1);
            currentProducts = activeProducts;
            setPublicProducts(activeProducts);
            setProductsLoaded(true);
          } catch (error) {
            console.error('Error cargando productos:', error);
            setChatLog(prev => [...prev, { sender: 'bot', text: 'Lo siento, no pude cargar los productos en este momento.' }]);
            setIsTyping(false);
            return;
          }
        }
        
        await delay(800);
        
        // Construir mensaje con nombres de productos usando los datos actuales
        let productList = 'Servicios disponibles:\n\n';
        if (currentProducts.length === 0) {
          productList = 'No hay servicios disponibles ahora.\n\n';
        } else {
          currentProducts.forEach(p => {
            productList += `• ${p.Nombre}\n`;
          });
        }
        productList += '\n¿Quieres ver más detalles?';
        
        setChatLog(prev => [...prev, { sender: 'bot', text: productList }]);
        setIsTyping(false);
        setPendingNav('show-products');
        return;
      }
      
      // Respuesta a confirmación de mostrar productos
      if (pendingNav === 'show-products') {
        if (/^(s[ií]|si|claro|ok|dale|ver|mostrar|muestra|yes)\b/i.test(lower)) {
          if (typeof onCloseLoginModal === 'function') {
            try { onCloseLoginModal(); } catch {}
          }
          setIsTyping(true);
          setPendingNav({ route: '/productos', finalText: 'Aquí están todos los servicios.' });
          navigate('/productos');
          return;
        }
        if (/^no\b/i.test(lower)) {
          setChatLog(prev => [...prev, { sender: 'bot', text: '¿En qué más te ayudo?' }]);
          setPendingNav(null);
          return;
        }
        setChatLog(prev => [...prev, { sender: 'bot', text: '¿Ver los detalles? (sí/no)' }]);
        return;
      }

      if (isBookingsAdminIntent(lower)) {
        if (isAdmin()) {
          const ok = await ensureActiveUser(); if (!ok) return;
          setIsTyping(true);
          setPendingNav({ route: '/admin/bookings', finalText: 'Reservas del negocio.' });
          navigate('/admin/bookings');
        } else {
          // Para usuarios cliente, interpreta "reservas" como "mis reservas"
          if (isLoggedIn()) {
            setIsTyping(true);
            setPendingNav({ route: '/mis-reservas', finalText: 'Aquí están tus reservas.' });
            navigate('/mis-reservas');
          } else {
            setIsTyping(true); await delay(1200);
            setChatLog(prev => [...prev, { sender: 'bot', text: 'Para ver tus reservas, inicia sesión por favor.' }]);
            setIsTyping(false);
            if (typeof onLoginRequest === 'function') onLoginRequest();
          }
        }
        return;
      }

      if (isAgendaIntent(lower)) {
        if (isAdmin()) {
          const ok = await ensureActiveUser(); if (!ok) return;
          setIsTyping(true);
          setPendingNav({ route: '/admin/agenda', finalText: 'Agenda.' });
          navigate('/admin/agenda');
        } else {
          setIsTyping(true); await delay(800);
          setChatLog(prev => [...prev, { sender: 'bot', text: '¿Te interesa ver nuestros servicios o productos?' }]);
          setIsTyping(false);
        }
        return;
      }

      setIsTyping(true);
      const start = Date.now();

      // Real request
      let replyText = '';
      try {
        const response = await axios.post(`${API_BASE}/api/chatbot`, {
          message: messageToSend,
          isLoggedIn: isLoggedIn()
        });
        if (response.data && response.data.reply) {
          replyText = response.data.reply;
        } else {
          throw new Error('Respuesta inválida del servidor');
        }
      } catch (err) {
        console.error('Error al enviar mensaje a Mora.artt:', err);
        replyText = 'Lo siento, no puedo procesar tu pregunta ahora. Intenta más tarde.';
      }

      // Garantiza al menos 3s de animación de escritura
      const elapsed = Date.now() - start;
      if (elapsed < 3000) await delay(3000 - elapsed);

      setChatLog(prev => [...prev, { sender: 'bot', text: replyText }]);
    } catch (error) {
      // Fallback extra (raro que llegue aquí por el catch interno)
      const errorMsg = 'Lo siento, no puedo procesar tu pregunta ahora. Intenta más tarde.';
      setChatLog(prev => [...prev, { sender: 'bot', text: errorMsg }]);
      console.error('Fallo general en ChatBotUI:', error);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-4 rounded shadow-lg" style={{ backgroundColor: '#e5d7f9' }}>
      <h3 className="text-lg font-semibold mb-1" style={{ color: '#4b2c83' }}>Mora ❤️</h3>
      <div style={{ color: '#6f4ea3', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
        Asistente virtual de Mora.artt
      </div>

      <div
        className="h-64 overflow-y-auto mb-3 border rounded p-2"
        style={{
          backgroundColor: '#dbc7f7',
          borderColor: 'rgba(130,40,200,0.4)',
          borderStyle: 'solid',
          borderWidth: '1px',
          color: '#4b2c83',
          overflowX: 'hidden' // evita scroll horizontal
        }}
        lang="es" // ayuda si en <html> no está definido el idioma
      >
        {chatLog.map((msg, idx) => (
          <div
            key={idx}
            className={`mb-2 flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className="px-5 py-3 rounded-lg max-w-[96%] md:max-w-[92%] whitespace-pre-wrap"
              role="article"
              aria-label={msg.sender === 'user' ? 'Mensaje del usuario' : 'Respuesta del asistente'}
              style={{
                background: msg.sender === 'user'
                  ? 'linear-gradient(135deg, #a984f5, #8b63e8)'
                  : 'linear-gradient(135deg, #dbc7f7, #c3a9f3)',
                color: msg.sender === 'user' ? '#f0eaff' : '#3e2a6b',
                boxShadow: msg.sender === 'user'
                  ? '0 4px 10px rgba(139, 99, 232, 0.5)'
                  : '0 4px 10px rgba(171, 146, 246, 0.4)',
                // Evitar cortar sílabas: sin guiones automáticos
                hyphens: 'none',
                WebkitHyphens: 'none',
                MozHyphens: 'none',
                // Romper solo cuando la palabra no cabe
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                lineHeight: 1.55,
                letterSpacing: '0.005em'
              }}
              lang="es"
            >
              <span style={{ display: 'inline-block', overflowWrap: 'break-word' }}>
                <Linkify>{msg.text}</Linkify>
              </span>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="mb-2 flex justify-start" role="status" aria-live="polite">
            <div
              className="px-5 py-3 rounded-lg max-w-[96%] md:max-w-[92%] whitespace-pre-wrap"
              style={{
                background: 'linear-gradient(135deg, #dbc7f7, #c3a9f3)',
                color: '#3e2a6b',
                boxShadow: '0 4px 10px rgba(171, 146, 246, 0.4)'
              }}
            >
              <span>Mora está escribiendo</span>
              <span className="typing-dots" aria-hidden="true" style={{ marginLeft: 8 }}>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
          placeholder="Escribe tu pregunta..."
          aria-label="Escribe tu pregunta"
          autoComplete="off"
          autoFocus
          ref={inputRef}
          style={{
            flexGrow: 1,
            borderRadius: '8px',
            padding: '10px',
            border: '1px solid rgba(130,40,200,0.5)',
            outlineColor: '#8b63e8',
            color: '#4b2c83',
            backgroundColor: '#f3eaff',
          }}
        />
        <button
          type="button"
          onClick={sendChatMessage}
          disabled={!chatInput.trim()}
          aria-disabled={!chatInput.trim()}
          aria-label="Enviar pregunta"
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 600,
            color: '#f0eaff',
            background: chatInput.trim()
              ? 'linear-gradient(135deg, #8b63e8, #a984f5)'
              : '#c5baf7',
            border: 'none',
            cursor: chatInput.trim() ? 'pointer' : 'not-allowed',
            transition: 'background 0.3s ease',
          }}
          onMouseEnter={e => {
            if (chatInput.trim()) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #a984f5, #8b63e8)';
            }
          }}
          onMouseLeave={e => {
            if (chatInput.trim()) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #8b63e8, #a984f5)';
            }
          }}
        >
          Preguntar
        </button>
      </div>
    </div>
  );
};

export default ChatBotUI;
